/**
 * Helper functions for generating and managing IDs in the NFL stats system.
 * These functions handle the creation and manipulation of various identifiers
 * used throughout the application, including week numbers, UIDs, and season-week IDs.
 * @module id-helpers
 */
import { GameWeek, Season } from '../constants/id-constants';
import { TeamAbrvToTeamID, TeamNamesByID, TID_LIST } from '../constants/league-constants';
import { ITeamLineScore } from '../types';
import {
  ByeWeekID,
  DecomposedUID,
  DeconstructedByeWeekID,
  DeconstructedGameID,
  DeconstructedGameWithScoreByQtrID,
  DeconstructedGameWithScoreID,
  DeconstructedHeadToHeadID,
  DeconstructedSeasonTeamAbrvID,
  DeconstructedSeasonWeekID,
  DeconstructedSeasonWeekTeamAbrvID,
  DeconstructedTeamAbrvID,
  DeconstructedTID,
  GameID,
  GameWeekKey,
  GameWithScoreByQtrID,
  GameWithScoreID,
  HeadToHeadID,
  SeasonID,
  SeasonTeamAbrvID,
  SeasonWeekID,
  SeasonWeekTeamAbrvID,
  StringOrNumber,
  TeamAbrvID,
  TeamAbrvType,
  TeamID,
  TID,
  UID,
  WeekID,
} from '../types/id.types';
import { getBelongsToSeasonDate, getGameWeekKeyByDateString } from './date-helpers';
import { getTeamScoringByQtrString } from './predictive-analysis-helpers';
// Generic types for the app
export type Maybe<T> = T | undefined;
/**
 * Converts a weekID string to its numerical representation.
 * Handles various formats including uppercase "Week1", "Week17",
 * lowercase "week1", spaces, and direct numbers between 1 and 18.
 * "week 18" and "all".
 *
 * @param {WeekID} weekID - The week identifier (e.g., "Week1", "week 17")
 *
 * @return {number} The numerical value of the week (e.g., 1, 17)
 * @example
 * toNumber("Week1") // returns 1
 * toNumber("Week17") // returns 17
 */
export const toNumber = (weekID: WeekID): number => {
  if (typeof weekID === 'number' && weekID >= 1 && weekID <= 18) {
    return weekID;
  } else if (weekID === 'all') {
    return 1;
  } else if (typeof weekID === 'string') {
    const weekString = weekID.toLowerCase().replace(' ', '').replace('week', '');
    const result = parseInt(weekString, 10);
    if (Number.isNaN(result)) {
      const msg = `toNumber: Invalid weekID string format ${weekID}`;
      console.warn(`⚠️ ${msg}`);
      return 1;
    }
    return result;
  }
  return 1; // Default to 1 if input is invalid
};

export const getSeasonIdFromGameID = (gameID: string): SeasonID => {
  const [gameDate] = gameID.split('_');
  return `${getBelongsToSeasonDate(gameDate).getFullYear()}`;
};

/**
 * Returns the `GameWeekKey` for a given `gameID`.
 *
 * @param  {GameID} gameID - formatted as 'YYYYMMDD_AWAY@HOME'
 *
 * @return {GameWeekKey} `GameWeekKey` is a key in `GameWeeks`. Format is 'WeekX' where X is the week number between 1 and 18
 */
export const getGameWeekKeyByGameID = (gameID: GameID): GameWeekKey => {
  const dateString = gameID.split('_')[0]; // 20240912_BUF@MIA
  return getGameWeekKeyByDateString(dateString); // 20240912
};

/**
 * Creates a formatted prefix for ordered UIDs by padding both week and team IDs with leading zeros.
 * This ensures consistent sorting and display of IDs in the system.
 *
 * @param {WeekID} weekID - The week identifier (e.g., "Week1")
 * @param {TeamID} teamID - The team identifier
 *
 * @return {string} A formatted string in the pattern "DD-DD" where D represents digits
 * @example
 * getPrefixForOrderedUID("Week1", "5") // returns "01-05"
 * getPrefixForOrderedUID("Week17", "12") // returns "17-12"
 */
export const getPrefixForOrderedUID = (weekID: WeekID, teamID: TeamID): string => {
  const weekIDNum = toNumber(weekID);
  const OrderedWeekID = `${weekIDNum}`.padStart(2, '0');
  const OrderedTeamID = `${teamID}`.padStart(2, '0');
  return `${OrderedWeekID}-${OrderedTeamID}`;
};

/**
 * Generates a unique identifier (UID) for a specific game and team combination.
 * The UID follows a specific format to ensure uniqueness and maintain order:
 * YYYY-DD-DD-weekID-teamID-TeamAbrv-gameID
 *
 * @param {WeekID} weekID - The week identifier (e.g., "Week1")
 * @param {TeamID} teamID - The team identifier
 * @param {GameID} gameID - The game identifier
 * @param {SeasonID} [yearID='2025'] - The season year, defaults to '2025' if not provided
 *
 * @return {UID} A unique identifier combining all components
 * @example
 * toUID("Week1", "5", "20250901_SF@ARI")
 * // returns "2025-01-05-Week1-5-SF-20250901_SF@ARI"
 */
export const toUID = (weekID: WeekID, teamID: TeamID, gameID: GameID, yearID: SeasonID = Season.Season2025): UID => {
  let seasonID = yearID;
  if (gameID !== 'BYE') {
    seasonID = getSeasonIdFromGameID(gameID);
  }
  const orderedPair = getPrefixForOrderedUID(weekID, teamID);
  return `${seasonID}-${orderedPair}-${weekID}-${teamID}-${TeamNamesByID[parseInt(teamID)]}-${gameID}`;
};

/**
 * Breaks down a UID into its component parts.
 * Handles both old format (without year prefix) and new format UIDs.
 *
 * @param {UID} orderedUID - The UID to decompose
 *
 * @return {DecomposedUID} An object containing yearID, weekNum, teamNum, weekID, teamID, teamAbrv, gameID, and seasonWeekID
 *
 * @throws Error if UID format is invalid
 *
 * @example
 * breakUID('2025-01-16-Week1-16-KC-20250905_KC@BAL');
 * // returns { yearID: '2025', weekNum: 1, teamNum: 16, weekID: 'Week1', teamID: '16', ... }
 */
export const breakUID = (orderedUID: UID): DecomposedUID => {
  let uID = orderedUID;
  if (orderedUID.charAt(2) === '-') {
    const splitted = orderedUID.split('-');
    const gameID = splitted[splitted.length - 1];
    const YYYY = getSeasonIdFromGameID(gameID);
    uID = `${YYYY}-${orderedUID}`;
  }
  // regex to check for string 2024-09-15-Week9-15-JAX-20241103_JAX@PHI, 2024-09-27-Week9-27-PHI-20241103_JAX@PHI, etc
  const regex = /(\d{4})-(\d{2})-(\d{2})-Week(\d{1,2})-(\d{1,2})-(\w{2,3})-(\d{8}_\w{2,3}@\w{2,3})/;
  // regex to check for string 2024-09-15-Week9-15-JAX-BYE
  const regexBye = /(\d{4})-(\d{2})-(\d{2})-Week(\d{1,2})-(\d{1,2})-(\w{2,3})-BYE/;

  // combine the two previous regexes
  const regexCombined = new RegExp(`(${regex.source})|(${regexBye.source})`);

  // test for the regex
  if (!regexCombined.test(uID)) {
    throw new Error('Invalid orderedUID format');
  }

  const [yearID, weekNum, teamNum, weekID, teamID, teamAbrv, gameID] = uID.split('-');
  const seasonWeekID = toSeasonWeekID(yearID as SeasonID, toWeekID(weekNum));
  return {
    yearID,
    weekNum: parseInt(weekNum, 10),
    teamNum: parseInt(teamNum, 10),
    weekID,
    teamID,
    teamAbrv,
    gameID,
    seasonWeekID,
  };
};

// #region SeasonWeekID *******************************************************

/**
 * Returns a unique ID that combines SeasonID and a zero-padded WeekID.
 * Format: YYYY-WW (e.g., '2025-05', '2025-17')
 *
 * @param {SeasonID} year - The season year (e.g., '2025')
 * @param {WeekID} week - The week identifier (e.g., 'Week1', 'Week17')
 *
 * @return {SeasonWeekID} A formatted SeasonWeekID string in YYYY-WW format
 *
 * @example
 * toSeasonWeekID('2025', 'Week5'); // returns '2025-05'
 * toSeasonWeekID('2025', 'Week17'); // returns '2025-17'
 */
export const toSeasonWeekID = (year: SeasonID, week: WeekID): SeasonWeekID => {
  const weekNum = toNumber(week);
  return `${year}-${String(weekNum).padStart(2, '0')}` as SeasonWeekID;
};

/**
 * Breaks down a SeasonWeekID into its constituent parts.
 *
 * @param {SeasonWeekID} seasonWeekID - The SeasonWeekID to break down (format: YYYY-WW)
 *
 * @return {DeconstructedSeasonWeekID} An object containing yearID, weekID, zeroPaddedWeekID, and numericWeekID
 *
 * @example
 * breakSeasonWeekID('2025-05');
 * // returns { yearID: '2025', weekID: 'Week5', zeroPaddedWeekID: '05', numericWeekID: 5 }
 */
export const breakSeasonWeekID = (seasonWeekID: SeasonWeekID): DeconstructedSeasonWeekID => {
  const [year, week] = seasonWeekID.split('-');
  return { yearID: year, weekID: toWeekID(week), zeroPaddedWeekID: week, numericWeekID: parseInt(week, 10) };
};
// #endregion

/**
 * Extracts and constructs a SeasonWeekID from a GameID.
 *
 * @param {GameID} gameID - The game identifier (e.g., '20250905_KC@BAL')
 *
 * @return {SeasonWeekID} SeasonWeekID in YYYY-WW format
 *
 * @example
 * toSeasonWeekIDByGameID('20250905_KC@BAL'); // returns '2025-01'
 */
export const toSeasonWeekIDByGameID = (gameID: GameID): SeasonWeekID => {
  const seasonID = getSeasonIdFromGameID(gameID);
  const weekID = getGameWeekKeyByGameID(gameID);
  return toSeasonWeekID(seasonID, weekID);
};

/**
 * Converts a week, team, and season ID into a bye week ID.
 * Format: YYYY-WW-NN-AAA (e.g., '2025-05-16-KC')
 *
 * @param {WeekID} weekID - The week identifier (e.g., 'Week5')
 * @param {TeamID} teamID - The team ID (e.g., '16')
 * @param {SeasonID} seasonID - The season ID (e.g., '2025')
 *
 * @return {ByeWeekID} The bye week ID in YYYY-WW-NN-AAA format
 *
 * @example
 * toByeWeekID('Week5', '16', '2025'); // returns '2025-05-16-KC'
 * toByeWeekID('Week7', '12', '2025'); // returns '2025-07-12-BUF'
 */
export const toByeWeekID = (weekID: WeekID, teamID: TeamID, seasonID: SeasonID): ByeWeekID => {
  const weekNum = `${toNumber(weekID)}`.padStart(2, '0');
  const teamNum = `${teamID}`.padStart(2, '0');
  const teamAbrv = TeamNamesByID[parseInt(teamID, 10)] || 'UNK';
  return `${seasonID}-${weekNum}-${teamNum}-${teamAbrv}`;
};

/**
 * Breaks down a ByeWeekID into its constituent parts.
 *
 * @param {ByeWeekID} byeWeekID - The ByeWeekID to break down (format: YYYY-WW-NN-AAA)
 *
 * @return {DeconstructedByeWeekID} An object containing seasonID, weekID, teamID, teamAbrv, seasonWeekID, and seasonTeamAbrvID
 *
 * @example
 * breakByeWeekID('2025-05-16-KC');
 * // returns { seasonID: '2025', weekID: 'Week5', teamID: '16', teamAbrv: 'KC', ... }
 */
export const breakByeWeekID = (byeWeekID: ByeWeekID): DeconstructedByeWeekID => {
  const [seasonID, week, team, teamAbrv] = byeWeekID.split('-');
  const teamID = toTeamID(team);
  const weekID = toWeekID(week);
  return {
    seasonID: seasonID as SeasonID,
    weekID,
    teamID,
    teamAbrvID: toTeamAbrvID(teamID),
    teamAbrv: teamAbrv as TeamAbrvType,
    seasonWeekID: toSeasonWeekID(seasonID as SeasonID, weekID),
    seasonTeamAbrvID: toSeasonTeamAbrvID(seasonID, teamID),
  };
};

/**
 * Converts a team number string to a TeamID by removing leading zeros.
 *
 * @param {string} teamNumAsStr - The team number as a string (may have leading zeros)
 *
 * @return {TeamID} The TeamID as a string without leading zeros
 *
 * @example
 * toTeamID('05'); // returns '5'
 * toTeamID('16'); // returns '16'
 */
export const toTeamID = (teamNumAsStr: string): TeamID => parseInt(teamNumAsStr, 10).toString();
// #endregion

/**
 * Converts the current week ID to the previous week ID.
 * Returns the same week if already at Week1.
 *
 * @param {GameWeek} currentWeek - The current week ID (e.g., 'Week2')
 *
 * @return {GameWeek} The previous week ID (e.g., 'Week1')
 *
 * @example
 * toPreviousWeekID('Week5'); // returns 'Week4'
 * toPreviousWeekID('Week1'); // returns 'Week1'
 */
export const toPreviousWeekID = (currentWeek: GameWeek): GameWeek => {
  const weekNum = toNumber(currentWeek);
  if (weekNum > 1) {
    return toWeekID(weekNum - 1);
  }
  return currentWeek;
};

/**
 * Converts a week number or string to a GameWeek enum value.
 *
 * @param {number | string} weekValue - Week as number (1-18) or string ('Week1', '5', etc.)
 *
 * @return {GameWeek} GameWeek enum value
 *
 * @throws Error if weekValue is outside valid range (1-18)
 *
 * @example
 * toWeekID(5); // returns GameWeek.Week5
 * toWeekID('Week17'); // returns GameWeek.Week17
 * toWeekID('5'); // returns GameWeek.Week5
 */
export const toWeekID = (weekValue: number | string): GameWeek => {
  let weekNum = 0;
  if (typeof weekValue === 'string') {
    // Default to Week 1 only for completely empty input
    if (weekValue === '') {
      weekNum = 1;
    } else {
      const weekString = weekValue.toLowerCase().replaceAll(' ', '').replace('week', '');
      weekNum = parseInt(weekString, 10);
    }
  } else {
    weekNum = weekValue;
  }

  const isWeekValueNumber = typeof weekNum === 'number' && !Number.isNaN(weekNum);
  if (isWeekValueNumber) {
    if (weekNum >= 1 && weekNum <= 18) {
      return GameWeek[`Week${weekNum}` as keyof typeof GameWeek];
    }
  }

  const errorMsg = `From toWeekID - Invalid week value: <${weekValue}>`;
  const errorReason = 'Must be a number, or a string in the format "WeekX" where X is between 1 and 18.';
  const fullErrorMsg = `${errorMsg} ${errorReason}`;
  throw new Error(fullErrorMsg);
};

/**
 * Gets the next week ID from the current week.
 * Returns the same week if already at Week18.
 *
 * @param {GameWeek} currentWeek - The current week ID
 *
 * @return {GameWeek} The next week ID
 *
 * @example
 * getNextWeekID('Week5'); // returns 'Week6'
 * getNextWeekID('Week18'); // returns 'Week18'
 */
export const getNextWeekID = (currentWeek: GameWeek): GameWeek => {
  const weekNum = toNumber(currentWeek);
  if (weekNum < 18) {
    return toWeekID(weekNum + 1);
  }
  return currentWeek;
};

/**
 * Converts a TeamID to a 4-character zero-padded string.
 *
 * @param {TeamID} teamID - The team identifier
 *
 * @return {string} A 4-character zero-padded team ID string
 *
 * @example
 * to4CharTeamID('5'); // returns '0005'
 * to4CharTeamID('16'); // returns '0016'
 */
export const to4CharTeamID = (teamID: TeamID) => teamID.padStart(4, '0');

// #region TEAM ID HELPER FUNCTIONS *******************************************
/**
 * Creates a TID from a TeamID by looking up the team abbreviation.
 *
 * @param {TeamID} teamID - The team identifier
 *
 * @return {TID} A TID in format 'XXXX-TEAMABRV'
 *
 * @example
 * toTIDByTeamID('16'); // returns '0016-KC'
 */
export const toTIDByTeamID = (teamID: TeamID) => {
  const teamAbrv = TeamNamesByID[parseInt(teamID, 10)];
  return toTID(teamAbrv, teamID);
};
/**
 * Creates a TID from a team abbreviation by looking up the team ID.
 *
 * @param {string} teamAbrv - The team abbreviation (e.g., 'KC', 'BUF')
 *
 * @return {TID} A TID in format 'XXXX-TEAMABRV'
 *
 * @example
 * toTIDByAbrv('KC'); // returns '0016-KC'
 */
export const toTIDByAbrv = (teamAbrv: string) => {
  const index = TeamNamesByID.indexOf(teamAbrv);
  return toTID(teamAbrv, String(index));
};

/**
 * Breaks down a TID into its constituent parts.
 *
 * @param {TID} tID - The TID to break down (format: 'XXXX-TEAMABRV')
 *
 * @return {DeconstructedTID} An object containing team4CharID, teamAbrv, and teamNumID
 *
 * @example
 * breakTID('0016-KC'); // returns { team4CharID: '0016', teamAbrv: 'KC', teamNumID: 16 }
 */
export const breakTID = (tID: TID): DeconstructedTID => {
  const [team4CharID, teamAbrv] = tID.split('-');
  return { team4CharID, teamAbrv, teamNumID: parseInt(team4CharID) };
};
const isValidTID = (value: string): value is TID => {
  return TID_LIST.includes(value as TID);
};
/**
 * Converts team abbreviation and team ID into a TID.
 * Format: XXXX-TEAMABRV where XXXX is 4-character zero-padded team ID.
 *
 * @param {string} teamAbrv - Team abbreviation (e.g., 'KC', 'ARI')
 * @param {TeamID} teamID - Team ID
 *
 * @return {TID} The TID in format 'XXXX-TEAMABRV'
 *
 * @example
 * toTID('ARI', '1'); // returns '0001-ARI'
 * toTID('KC', '16'); // returns '0016-KC'
 * toTID('WSH', '32'); // returns '0032-WSH'
 */
export const toTID = (teamAbrv: string, teamID: TeamID): TID => {
  const tID = `${to4CharTeamID(teamID)}-${teamAbrv}`;
  return isValidTID(tID) ? tID : TID_LIST[Math.max(parseInt(teamID) - 1, 0)];
};
// #endregion

// #region SeasonTeamAbrvID ***************************************************

/**
 * Creates a unique ID combining SeasonID with zero-padded teamID and team abbreviation.
 * Format: YYYY-NN-AAA (e.g., '2025-16-KC')
 *
 * @param {SeasonID} seasonID - The season year (e.g., '2025')
 * @param {TeamID} teamID - The team identifier
 *
 * @return {SeasonTeamAbrvID} A formatted SeasonTeamAbrvID string
 *
 * @example
 * toSeasonTeamAbrvID('2025', '16'); // returns '2025-16-KC'
 * toSeasonTeamAbrvID('2025', '5'); // returns '2025-05-ARI'
 */
export const toSeasonTeamAbrvID = (seasonID: SeasonID, teamID: TeamID): SeasonTeamAbrvID => {
  return `${seasonID}-${teamID.padStart(2, '0')}-${TeamNamesByID[parseInt(teamID)] as TeamAbrvType}`;
};

/**
 * Breaks down a SeasonTeamAbrvID into its constituent parts.
 *
 * @param {SeasonTeamAbrvID} seasonTeamAbrvID - The ID to break down (format: YYYY-NN-AAA)
 *
 * @return {DeconstructedSeasonTeamAbrvID} An object containing seasonID, teamID, teamAbrvID, and teamAbrv
 *
 * @example
 * breakSeasonTeamAbrvID('2025-16-KC');
 * // returns { seasonID: '2025', teamID: '16', teamAbrvID: '16-KC', teamAbrv: 'KC' }
 */
export const breakSeasonTeamAbrvID = (seasonTeamAbrvID: SeasonTeamAbrvID): DeconstructedSeasonTeamAbrvID => {
  const [seasonID, teamIDWithZeroPadding, teamAbrv] = seasonTeamAbrvID.split('-');
  const teamAbrvID = toTeamAbrvID(teamIDWithZeroPadding);
  const teamID = parseInt(teamIDWithZeroPadding).toString() as TeamID;
  return {
    seasonID,
    teamID,
    teamAbrvID,
    teamAbrv: teamAbrv as TeamAbrvType,
  };
};

/**
 * Generates an array of all 32 team SeasonTeamAbrvIDs for a given season.
 *
 * @param {SeasonID} seasonID - The season year (e.g., '2025')
 *
 * @return {SeasonTeamAbrvID[]} Array of all 32 SeasonTeamAbrvIDs for the season
 *
 * @example
 * getAllTeamsSeasonTeamAbrvIDs('2025');
 * // returns ['2025-01-ARI', '2025-02-ATL', ..., '2025-32-WSH']
 */
export const getAllTeamsSeasonTeamAbrvIDs = (seasonID: SeasonID): SeasonTeamAbrvID[] => {
  return Object.entries(TeamAbrvToTeamID).map(([, teamID]) => {
    return toSeasonTeamAbrvID(seasonID, teamID);
  });
};
// #endregion

// #region SeasonWeekTeamAbrvID ***********************************************

/**
 * Creates a unique ID combining SeasonID, zero-padded WeekID, TeamID and team abbreviation.
 * Format: YYYY-WW-NN-AAA (e.g., '2025-05-16-KC')
 *
 * @param {SeasonID} seasonID - The season year (e.g., '2025')
 * @param {WeekID} weekID - The week identifier (e.g., 'Week5')
 * @param {TeamID} teamID - The team identifier
 *
 * @return {SeasonWeekTeamAbrvID} A formatted SeasonWeekTeamAbrvID string
 *
 * @example
 * toSeasonWeekTeamAbrvID('2025', 'Week5', '16'); // returns '2025-05-16-KC'
 * toSeasonWeekTeamAbrvID('2025', 'Week17', '12'); // returns '2025-17-12-BUF'
 */
export const toSeasonWeekTeamAbrvID = (seasonID: SeasonID, weekID: WeekID, teamID: TeamID): SeasonWeekTeamAbrvID => {
  return `${seasonID}-${String(toNumber(weekID)).padStart(2, '0')}-${teamID.padStart(2, '0')}-${TeamNamesByID[parseInt(teamID)] as TeamAbrvType}`;
};

/**
 * Breaks down a SeasonWeekTeamAbrvID into its constituent parts.
 *
 * @param {SeasonWeekTeamAbrvID} seasonWeekTeamAbrvID - The ID to break down (format: YYYY-WW-NN-AAA)
 *
 * @return {DeconstructedSeasonWeekTeamAbrvID} An object containing yearID, seasonID, seasonWeekID, weekID, numericWeekID, zeroPaddedWeekID, teamID, teamAbrvID, and teamAbrv
 *
 * @example
 * breakSeasonWeekTeamAbrvID('2025-05-16-KC');
 * // returns { yearID: '2025', seasonID: '2025', seasonWeekID: '2025-05', weekID: 'Week5', ... }
 */
export const breakSeasonWeekTeamAbrvID = (
  seasonWeekTeamAbrvID: SeasonWeekTeamAbrvID,
): DeconstructedSeasonWeekTeamAbrvID => {
  const [seasonID, zeroPaddedWeekID, teamIDWithZeroPadding, teamAbrv] = seasonWeekTeamAbrvID.split('-');
  const teamAbrvID = toTeamAbrvID(teamIDWithZeroPadding);
  const teamID = parseInt(teamIDWithZeroPadding).toString() as TeamID;
  const weekID = toWeekID(zeroPaddedWeekID);
  const numericWeekID = parseInt(zeroPaddedWeekID, 10);
  return {
    yearID: seasonID,
    seasonID,
    seasonWeekID: toSeasonWeekID(seasonID, weekID),
    seasonTeamAbrvID: toSeasonTeamAbrvID(seasonID, teamID),
    weekID,
    numericWeekID,
    zeroPaddedWeekID,
    teamID,
    teamAbrvID,
    teamAbrv: teamAbrv as TeamAbrvType,
  };
};

// #endregion

/**
 * Returns the `GameWeekKey` for a given `gameID`.
 *
 * @param  {GameID} gameID - formatted as 'YYYYMMDD_AWAY@HOME'
 *
 * @return {GameWeekKey} `GameWeekKey` is a key in `GameWeeks`. Format is 'WeekX' where X is the week number between 1 and 18
 */
export const getSeasonWeekIDByGameID = (gameID: GameID): SeasonWeekID => {
  if (gameID) {
    const week = getGameWeekKeyByGameID(gameID);
    const season = getSeasonIdFromGameID(gameID);
    return toSeasonWeekID(season, week);
  }
  return toSeasonWeekID(Season.Season2025, GameWeek.Week1);
};

// #region TeamAbrvID *********************************************************

/**
 * Creates a unique ID with zero-padded teamID and team abbreviation.
 * Format: NN-AAA (e.g., '01-ARI', '16-KC')
 *
 * @param {TeamID} teamID - The team identifier
 *
 * @return {TeamAbrvID} A TeamAbrvID string
 *
 * @example
 * toTeamAbrvID('1'); // returns '01-ARI'
 * toTeamAbrvID('16'); // returns '16-KC'
 */
export const toTeamAbrvID = (teamID: TeamID): TeamAbrvID => {
  return `${teamID.padStart(2, '0')}-${TeamNamesByID[parseInt(teamID)] as TeamAbrvType}`;
};

/**
 * Breaks down a TeamAbrvID into its constituent parts.
 *
 * @param {TeamAbrvID} teamAbrvID - The ID to break down (format: NN-AAA)
 *
 * @return {DeconstructedTeamAbrvID} An object containing teamID and teamAbrv
 *
 * @example
 * breakTeamAbrvID('16-KC'); // returns { teamID: '16', teamAbrv: 'KC' }
 */
export const breakTeamAbrvID = (teamAbrvID: TeamAbrvID): DeconstructedTeamAbrvID => {
  const [teamIDWithZeroPadding, teamAbrv] = teamAbrvID.split('-');
  const teamID = parseInt(teamIDWithZeroPadding).toString() as TeamID;
  return {
    teamID,
    teamAbrv: teamAbrv as TeamAbrvType,
  };
};
// #endregion

// #region GAME WITH SCORE IDs ************************************************
/**
 * Creates a GameWithScoreID by appending scores to a GameID.
 * Format: GameID=awayScore:homeScore (e.g., '20250905_KC@BAL=20:24')
 *
 * @param {GameID} gameID - The game identifier
 * @param {StringOrNumber} [awayScore=0] - Away team score
 * @param {StringOrNumber} [homeScore=0] - Home team score
 *
 * @return {GameWithScoreID} A GameWithScoreID string
 *
 * @example
 * toGameWithScoreID('20250905_KC@BAL', 20, 24); // returns '20250905_KC@BAL=20:24'
 */
export const toGameWithScoreID = (
  gameID: GameID,
  awayScore: StringOrNumber = 0,
  homeScore: StringOrNumber = 0,
): GameWithScoreID => {
  return `${gameID}=${awayScore}:${homeScore}`;
};

/**
 * Breaks down a GameWithScoreID into its constituent parts.
 * Extracts game info, team IDs, abbreviations, and scores.
 *
 * @param {GameWithScoreID} gameWithScoreID - The ID to break down (format: GameID=awayScore:homeScore)
 *
 * @return {DeconstructedGameWithScoreID} An object containing gameID, team IDs, abbreviations, TeamAbrvIDs, and scores
 *
 * @example
 * breakGameWithScoreID('20250904_DAL@PHI=20:24');
 * // returns { gameID: '20250904_DAL@PHI', homeTeamID: '28', awayTeamID: '10', ... }
 */
export const breakGameWithScoreID = (gameWithScoreID: GameWithScoreID): DeconstructedGameWithScoreID => {
  const [gameID, scorePart] = gameWithScoreID.split('=');
  const [awayScoreStr, homeScoreStr] = scorePart.split(':');
  const [gameDateString, teamsPart] = gameID.split('_');
  const [awayTeamAbrv, homeTeamAbrv] = teamsPart.split('@');
  const homeTeamID = TeamAbrvToTeamID[homeTeamAbrv as TeamAbrvType] as TeamID;
  const awayTeamID = TeamAbrvToTeamID[awayTeamAbrv as TeamAbrvType] as TeamID;

  return {
    gameID: gameID as GameID,
    homeTeamID: homeTeamID,
    awayTeamID: awayTeamID,
    homeTeamAbrv: homeTeamAbrv as TeamAbrvType,
    awayTeamAbrv: awayTeamAbrv as TeamAbrvType,
    homeTeamAbrvID: toTeamAbrvID(homeTeamID),
    awayTeamAbrvID: toTeamAbrvID(awayTeamID),
    homeTeamScore: parseInt(homeScoreStr),
    awayTeamScore: parseInt(awayScoreStr),
    gameDateString: gameDateString,
  };
};

/**
 * "20250904_DAL@PHI"
 * @param {GameID} gameID
 *
 * @return {DeconstructedGameID}
 */
export const breakGameID = (gameID: GameID): DeconstructedGameID => {
  const [gameDateString, teamsPart] = gameID.split('_');
  const [awayTeamAbrv, homeTeamAbrv] = teamsPart.split('@');
  if (!awayTeamAbrv || !homeTeamAbrv) {
    throw new Error(`Invalid GameID format <${gameID}>`);
  }

  // Validate team abbreviations exist in mapping
  if (!(awayTeamAbrv in TeamAbrvToTeamID) || !(homeTeamAbrv in TeamAbrvToTeamID)) {
    throw new Error(`Unrecognized team abbreviation in GameID <${gameID}>`);
  }

  const homeTeamID = TeamAbrvToTeamID[homeTeamAbrv as TeamAbrvType] as TeamID;
  const awayTeamID = TeamAbrvToTeamID[awayTeamAbrv as TeamAbrvType] as TeamID;

  return {
    gameID: gameID as GameID,
    homeTeamID: homeTeamID,
    awayTeamID: awayTeamID,
    homeTeamAbrv: homeTeamAbrv as TeamAbrvType,
    awayTeamAbrv: awayTeamAbrv as TeamAbrvType,
    homeTeamAbrvID: toTeamAbrvID(homeTeamID),
    awayTeamAbrvID: toTeamAbrvID(awayTeamID),
    gameDateString: gameDateString,
  };
};

// #endregion

// #region
/**
 * Creates a GameWithScoreByQtrID by appending quarter-by-quarter scores to a GameID.
 * Format: GameID=Q1,Q2,Q3,Q4[,OT],Total:Q1,Q2,Q3,Q4[,OT],Total
 *
 * @param {GameID} gameID - The game identifier
 * @param {ITeamLineScore} awayLineScore - Away team's line score by quarter
 * @param {ITeamLineScore} homeLineScore - Home team's line score by quarter
 *
 * @return {GameWithScoreByQtrID} A GameWithScoreByQtrID string
 *
 * @example
 * const awayScore = { Q1: '7', Q2: '10', Q3: '0', Q4: '3', totalPts: '20', ... };
 * const homeScore = { Q1: '3', Q2: '14', Q3: '7', Q4: '0', totalPts: '24', ... };
 * toGameWithScoreByQtrID('20250905_KC@BAL', awayScore, homeScore);
 * // returns '20250905_KC@BAL=7,10,0,3,20:3,14,7,0,24'
 */
export const toGameWithScoreByQtrID = (
  gameID: GameID,
  awayLineScore: ITeamLineScore,
  homeLineScore: ITeamLineScore,
): GameWithScoreByQtrID => {
  return `${gameID}=${getTeamScoringByQtrString(awayLineScore)}:${getTeamScoringByQtrString(homeLineScore)}`;
};

/**
 * Generates a team line score object from a comma-separated score string.
 * Parses quarterly scores and overtime if present.
 *
 * @param {string} teamScore - Comma-separated scores (e.g., '7,10,0,3,20' or '7,10,0,3,7,27')
 * @param {TeamID} teamID - The team identifier
 * @param {TeamAbrvType} teamAbv - The team abbreviation
 *
 * @return {ITeamLineScore} An ITeamLineScore object with quarterly breakdown
 *
 * @example
 * generateTeamLineScore('7,10,0,3,20', '16', 'KC');
 * // returns { Q1: '7', Q2: '10', Q3: '0', Q4: '3', totalPts: '20', ... }
 */
const generateTeamLineScore = (teamScore: string, teamID: TeamID, teamAbv: TeamAbrvType): ITeamLineScore => {
  const scoreList = teamScore.split(',');
  const totalPts = scoreList.pop() || '0';
  const OT = scoreList.length === 5 ? scoreList.pop() : undefined;
  const [Q1 = '0', Q2 = '0', Q3 = '0', Q4 = '0'] = scoreList;

  const result: ITeamLineScore = {
    Q1,
    Q2,
    Q3,
    Q4,
    OT,
    totalPts,
    teamID,
    teamAbv,
    currentlyInPossession: 'false',
  };

  return result;
};

/**
 * Breaks down a GameWithScoreByQtrID into its constituent parts.
 * Extracts game info, team data, and quarter-by-quarter scoring.
 *
 * @param {GameWithScoreByQtrID} gameWithScoreByQtrID - The ID to break down
 *
 * @return {DeconstructedGameWithScoreByQtrID} An object extending DeconstructedGameWithScoreID with homeTeamLineScore and awayTeamLineScore
 *
 * @example
 * breakGameWithScoreByQtrID('20250905_KC@BAL=7,10,0,3,20:3,14,7,0,24');
 * // returns { gameID: '20250905_KC@BAL', homeTeamLineScore: {...}, awayTeamLineScore: {...}, ... }
 */
export const breakGameWithScoreByQtrID = (
  gameWithScoreByQtrID: GameWithScoreByQtrID,
): DeconstructedGameWithScoreByQtrID => {
  const [gameID, scorePart] = gameWithScoreByQtrID.split('=');
  const [awayScoreStr, homeScoreStr] = scorePart.split(':');
  const awayScoreStrList = awayScoreStr.split(',');
  const homeScoreStrList = homeScoreStr.split(',');
  const awayTotalScore = awayScoreStrList[awayScoreStrList.length - 1];
  const homeTeamScore = homeScoreStrList[homeScoreStrList.length - 1];
  const result = breakGameWithScoreID(`${gameID}=${awayTotalScore}:${homeTeamScore}`);
  const homeTeamLineScore: ITeamLineScore = generateTeamLineScore(homeScoreStr, result.homeTeamID, result.homeTeamAbrv);
  const awayTeamLineScore: ITeamLineScore = generateTeamLineScore(awayScoreStr, result.awayTeamID, result.awayTeamAbrv);
  const seasonWeekID: SeasonWeekID = getSeasonWeekIDByGameID(result.gameID);
  return {
    ...result,
    homeTeamLineScore,
    awayTeamLineScore,
    seasonWeekID,
  };
};
// #endregion

export const getTeamIDsFromGameID = (gameID: GameID): [string, string] => {
  const [away, home] = gameID.split('_')[1].split('@');
  return [
    TeamAbrvToTeamID[home as keyof typeof TeamAbrvToTeamID],
    TeamAbrvToTeamID[away as keyof typeof TeamAbrvToTeamID],
  ];
};

/**
 * Creates a MatchupID from two TeamAbrvIDs.
 * Always returns teams in sorted order for consistency.
 *
 * @param {TeamAbrvID} team1 - First team abbreviation ID
 * @param {TeamAbrvID} team2 - Second team abbreviation ID
 *
 * @return {HeadToHeadID} Sorted matchup ID (e.g., '01-ARI_16-KC')
 *
 * @example
 * toMatchupID('16-KC', '01-ARI'); // Returns: '01-ARI_16-KC'
 * toHeadToHeadID('01-ARI', '16-KC'); // Returns: '01-ARI_16-KC' (same!)
 */
export const toHeadToHeadID = (team1: TeamAbrvID, team2: TeamAbrvID): HeadToHeadID => {
  const [first, second] = team1 < team2 ? [team1, team2] : [team2, team1];
  return `${first}_${second}`;
};

/**
 * Breaks down a HeadToHeadID into its constituent parts.
 *
 * @param {HeadToHeadID} headToHeadID - Matchup identifier (e.g., '01-ARI_16-KC')
 *
 * @return {DeconstructedHeadToHeadID} Object containing headToHeadID, team1AbrvID, team2AbrvID
 *
 * @example
 * breakHeadToHeadID('01-ARI_16-KC');
 * // Returns: { headToHeadID: '01-ARI_16-KC', team1AbrvID: '01-ARI', team2AbrvID: '16-KC' }
 */
export const breakHeadToHeadID = (headToHeadID: HeadToHeadID): DeconstructedHeadToHeadID => {
  const [team1AbrvID, team2AbrvID] = headToHeadID.split('_') as [TeamAbrvID, TeamAbrvID];
  return {
    headToHeadID,
    team1AbrvID,
    team2AbrvID,
  };
};
